import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { ContactItemDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { GetTableDataRequestModel } from '@/models/GetTableDataRequest.model.ts';
import { GetTableDataResponseModel } from '@/models/GetTableDataResponse.model.ts';
import { formatDateToBackend } from '@/utils/formatDate.util.ts';
import { ContactsSearchFormModel } from '@/views/contacts/components/table/model/ContactsSearchForm.model.ts';

export type GetContactsReq = GetTableDataRequestModel<ContactsSearchFormModel>;

export type GetContactsResponse = GetTableDataResponseModel<ContactItemDto>;

type Return = {
  getContacts: (req: GetContactsReq) => Promise<GetContactsResponse>;
  isLoading: boolean;
};

export const useGetContacts = (): Return => {
  const { contactsApi } = useApiClient();
  const { userId } = useUserContext();

  const { isPending, mutateAsync } = useMutation<GetContactsResponse, AxiosError, GetContactsReq>({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      const {
        data: { items, totalPages, totalResults },
      } = await contactsApi.searchUserContacts(req.page, req.pageSize, userId, {
        ...req.params,
        customerFullTextSearchInput: req.params.customerFullTextSearchInput ?? '',
        fullTextSearchInput: req.params.fullTextSearchInput ?? '',
        isAlreadyInCustomer: req.params.isAlreadyInCustomer?.value ?? undefined,
        isAlreadyInSupplier: req.params.isAlreadyInSupplier?.value ?? undefined,
        lastContactedFrom: formatDateToBackend(req.params.lastContactedFrom),
        lastContactedTo: formatDateToBackend(req.params.lastContactedTo),
        ownerIds: req.params.ownerIds.map(it => it.value),
        status: req.params.status?.value,
      });

      return {
        items,
        totalPages,
        totalResults,
      };
    },
    mutationKey: [QueryKeyEnum.CONTACTS.ALL],
  });

  return {
    getContacts: mutateAsync,
    isLoading: isPending,
  };
};
